import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { integrationConnectionContract } from "../common/contracts/integrationConnection.contract";
import { getBackendAPIClient } from "../lib/backendAPIClient";
import { useWorkspaceContext } from "../state/workspaceContext";
import { IntegrationConnectionDto } from "../common/dto/integrationConnection/integration-connection.dto";
import { IntegrationConnection } from "../common/types/integrationConnection";
import { clearIntegrationConnectionErrorInQueryData } from "../queries/entitiesAccounts";

export const integrationConnectionContractClient = getBackendAPIClient(
    integrationConnectionContract,
);

export const integrationConnectionQueryKeys = {
    all: ["integration-connection"] as const,
    query: (workspaceId: string) =>
        [...integrationConnectionQueryKeys.all, workspaceId] as const,
};

export function useIntegrationConnections() {
    const { activeWorkspaceId } = useWorkspaceContext();

    return useQuery({
        queryKey: integrationConnectionQueryKeys.query(activeWorkspaceId ?? ""),
        queryFn: async () => {
            if (!activeWorkspaceId) {
                return [];
            }
            const response =
                await integrationConnectionContractClient.getConnections({
                    params: {
                        workspaceId: activeWorkspaceId,
                    },
                });

            return response.body.connections.map(
                IntegrationConnectionDto.deserialize,
            );
        },
    });
}

export function useIntegrationConnectionFixingMutation(
    connection: IntegrationConnection,
) {
    const { activeWorkspaceId } = useWorkspaceContext();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof integrationConnectionContract.fixConnection
            >["body"],
        ) => {
            if (!activeWorkspaceId) {
                return;
            }
            await integrationConnectionContractClient.fixConnection({
                params: {
                    workspaceId: activeWorkspaceId,
                    connectionId: connection.id,
                },
                body,
            });
        },
        onSuccess: () => {
            clearIntegrationConnectionErrorInQueryData(
                queryClient,
                connection.id,
            );

            queryClient.setQueryData<IntegrationConnection[]>(
                integrationConnectionQueryKeys.query(activeWorkspaceId ?? ""),
                (data) =>
                    data?.map((checkedConnection) =>
                        checkedConnection.id === connection.id
                            ? {
                                  ...checkedConnection,
                                  error: null,
                              }
                            : checkedConnection,
                    ) ?? [],
            );
        },
    });
}
